/**
 * Mirrors rely-back `src/api/models/Content.js`:
 * - `value` (required): default HTML — English / fallback.
 * - `translations[]`: only `{ locale, value }` where `locale` is enum **es | ca | eu | pt**.
 */
export const CONTENT_LOCALE_CODES = ['en', 'es', 'ca', 'eu', 'pt'];

/** Top-level Formik keys (FormikEditor only reads `values[name]` — nested paths break). */
export const FORM_HTML_FIELD_BY_LOCALE = {
  en: 'htmlEn',
  es: 'htmlEs',
  ca: 'htmlCa',
  eu: 'htmlEu',
  pt: 'htmlPt',
};

/** Map stored locale strings onto internal segment keys (handles legacy `pt-PT` → `pt`). */
export function normalizeApiLocale(locale) {
  if (locale == null) return locale;
  const s = String(locale).toLowerCase();
  if (s === 'pt-pt' || s === 'pt') return 'pt';
  return String(locale);
}

function buildLocaleValuesFromContent(content) {
  const empty = CONTENT_LOCALE_CODES.reduce((acc, code) => {
    acc[code] = '';
    return acc;
  }, {});

  const isCreateShape =
    !content ||
    (content._id == null &&
      !content.name &&
      !content.value &&
      !(content.translations && content.translations.length));

  if (isCreateShape) {
    return empty;
  }

  empty.en = content.value || '';
  (content.translations || []).forEach(t => {
    const loc = normalizeApiLocale(t.locale);
    if (loc && Object.prototype.hasOwnProperty.call(empty, loc)) {
      empty[loc] = t.value || '';
    }
  });
  return empty;
}

/** Flat initial values for Formik (avoids nested `localeValues.en` + FormikEditor bug). */
export function buildInitialFormValues(content) {
  const lv = buildLocaleValuesFromContent(content);
  return {
    name: content?.name || '',
    htmlEn: lv.en,
    htmlEs: lv.es,
    htmlCa: lv.ca,
    htmlEu: lv.eu,
    htmlPt: lv.pt,
  };
}

/**
 * Only non-empty HTML per locale. API/Joi reject `translations[n].value` when empty;
 * omit locales the user has not filled (do not send `{ locale, value: '' }`).
 */
export function buildTranslationsBody(values, content) {
  return ['es', 'ca', 'eu', 'pt']
    .map(locale => {
      const field = FORM_HTML_FIELD_BY_LOCALE[locale];
      const prev = content?.translations?.find(t => normalizeApiLocale(t.locale) === locale);
      const raw = values[field] ?? '';
      const value = typeof raw === 'string' ? raw.trim() : String(raw);
      const payload = { locale, value };
      if (prev?._id) {
        payload._id = prev._id;
      }
      return payload;
    })
    .filter(({ value }) => value.length > 0);
}
